import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  TextField
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import { getUser, getToken } from '../../services/auth';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import UploadIcon from '@mui/icons-material/Upload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CalculateIcon from '@mui/icons-material/Calculate';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import Badge from '@mui/material/Badge';
import axios from 'axios';
import { CONFIG } from '../../config/config';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { uploadMetrics, triggerMetricsCompute, getUploadProgress, getComputeProgress, getReviewEntities } from '../../services/api';
import MetricsTemplateDownloadDialog from './MetricsTemplateDownloadDialog';

// interface ReviewEntity {
//   id: number;
//   name: string;
//   review_entity_type: string;
// }

// interface ReviewEntityType {
//   id: number;
//   name: string;
//   display_label: string;
//   category: string;
// }

// interface ReviewEntity {
//   id: number;
//   name: string;
//   review_entity_type: ReviewEntityType;
// }

interface SheetProgress {
  name: string;
  status: 'reading' | 'processing' | 'done' | 'error';
  started_at: string | null;
  completed_at: string | null;
  row_count: number;
  success_count: number;
  error_count: number;
  error: string | null;
}

interface UploadMetricsResponse {
  message: string;
  stats: {
    [key: string]: { total: number; success: number; failed: number } | undefined;
  };
}

interface ComputeProgressData {
  process_id?: number;
  /** Backend usually sends this; omit when payload is partial (e.g. WS/proxy). */
  status?: 'in_progress' | 'completed' | 'failed' | 'not_found' | string;
  phase?: 'fetching_inputs' | 'computing';
  total_dimensions?: number;
  dimensions_completed?: number;
  current_dimension: string | null;
  current_dimension_status: 'fetching' | 'computing' | 'done' | 'error' | null;
  dimensions_summary?: { [dim: string]: { status: string; time_taken?: number; rows_created?: number; error?: string } } | null;
  dimension_errors?: { [dim: string]: string } | null;
  total_rows_created?: number;
  error: string | null;
  elapsed_seconds?: number;
  message?: string;
  file_name?: string;
  triggered_at?: string;
  triggered_by?: number;
}

interface UploadProgressData {
  process_id?: number;
  status: 'in_progress' | 'completed' | 'failed' | 'not_found' | 'pending';
  total_sheets: number;
  current_sheet: string | null;
  current_sheet_status: 'reading' | 'processing' | 'done' | 'error' | null;
  current_sheet_total_rows: number;
  current_sheet_rows_processed: number;
  sheets: SheetProgress[];
  error: string | null;
  job_started_at?: string | null;
  job_completed_at?: string | null;
  file_name?: string;
  triggered_at?: string;
  triggered_by?: number;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ViewMetrics = () => {
  const navigate = useNavigate();
  // const [reviewEntities, setReviewEntities] = useState<ReviewEntity[]>([]);
  // const [selectedEntity, setSelectedEntity] = useState<number | ''>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Upload metrics state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [progressData, setProgressData] = useState<UploadProgressData | null>(null);
  const [metricsStats, setMetricsStats] = useState<UploadMetricsResponse['stats'] | null>(null);

  // Compute metrics state
  const [computeLoading, setComputeLoading] = useState<boolean>(false);
  const [computeError, setComputeError] = useState<string>('');
  const [computeSuccess, setComputeSuccess] = useState<string>('');
  const [computeProgressData, setComputeProgressData] = useState<ComputeProgressData | null>(null);

  // Manual refresh: progress is only updated when the user clicks Refresh.
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateEntities, setTemplateEntities] = useState<
    { id: number; name: string; org_entity_code?: string | null }[]
  >([]);

  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    getReviewEntities()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setTemplateEntities(
          list.map((e: { id: number; name: string; org_entity_code?: string | null }) => ({
            id: e.id,
            name: e.name,
            org_entity_code: e.org_entity_code
          }))
        );
      })
      .catch(() => setTemplateEntities([]));
  }, []);

  const user = getUser();
  const isAdmin = user?.roles?.some(role => role.role_name === 'ADMIN') || false;

  const refreshProgress = useCallback(async () => {
    setRefreshing(true);
    try {
      const [uploadRes, computeRes] = await Promise.allSettled([
        getUploadProgress(),
        getComputeProgress(),
      ]);
      if (!mountedRef.current) return;
      if (uploadRes.status === 'fulfilled') {
        const data: UploadProgressData = uploadRes.value.data;
        setProgressData(data && data.status !== 'not_found' ? data : null);
      }
      if (computeRes.status === 'fulfilled') {
        const data: ComputeProgressData = computeRes.value.data;
        setComputeProgressData(data && data.status !== 'not_found' ? data : null);
      }
    } catch { /* ignore */ }
    finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }, []);

  // On mount: fetch latest progress once.
  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  const handleViewMetricsData = () => {
    navigate('/view-metrics-data');
  };

  // const handleEntityChange = (event: SelectChangeEvent<number | ''>) => {
  //   setSelectedEntity(event.target.value as number);
  // };

  // const handleAutocompleteChange = (event: any, value: ReviewEntity | null) => {
  //   setSelectedEntity(value ? value.id : '');
  // };

  // const handleViewComputedMetrics = () => {
  //   if (isAdmin) {
  //     navigate('/view-all-computed-metrics');
  //   } else if (selectedEntity) {
  //     navigate(`/view-computed-metrics/${selectedEntity}`);
  //   }
  // };

  const handleViewComputedMetrics = () => {
    navigate('/view-computed-metrics');
  };

  // Upload metrics handlers
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
          file.type !== 'application/vnd.ms-excel') {
        setUploadError('Please upload only Excel files (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
      setUploadError('');
      setMetricsStats(null);
    }
  };

  const isActiveStatus = (s?: string) => s === 'in_progress' || s === 'pending';

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    setUploadError('');
    setUploadSuccess('');
    setUploadLoading(true);
    setProgressData(null);
    setMetricsStats(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await uploadMetrics(formData);
      if (response.data.process_id) {
        setProgressData({
          process_id: response.data.process_id,
          status: 'in_progress' as const,
          total_sheets: 0,
          current_sheet: null, current_sheet_status: null,
          current_sheet_total_rows: 0, current_sheet_rows_processed: 0,
          sheets: [],
          error: null,
          file_name: selectedFile.name,
        });
        setUploadSuccess('Upload started in the background. Check "Load & Compute History" for progress.');
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.detail || 'Failed to start upload');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleTriggerCompute = async () => {
    setComputeError('');
    setComputeSuccess('');
    setComputeLoading(true);

    try {
      const response = await triggerMetricsCompute();
      if (response.data?.process_id) {
        setComputeProgressData({
          process_id: response.data.process_id,
          status: 'in_progress',
          phase: 'fetching_inputs',
          total_dimensions: 0,
          dimensions_completed: 0,
          current_dimension: null,
          current_dimension_status: null,
          dimensions_summary: {},
          dimension_errors: {},
          total_rows_created: 0,
          error: null,
          elapsed_seconds: 0,
        });
        setComputeSuccess(
          'Metrics computation started in the background. Check "Load & Compute History" for progress.',
        );
      }
    } catch (err: any) {
      setComputeError(err.response?.data?.detail || 'Failed to trigger metrics computation');
    } finally {
      setComputeLoading(false);
    }
  };

  // Render functions for the cards
  const renderUploadCard = () => (
    <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <UploadIcon sx={{ mr: 1 }} /> Upload Review Entity Metrics
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload Excel file containing metrics data for review entities.
        </Typography>
        {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}
        {uploadSuccess && <Alert severity="success" sx={{ mb: 2 }}>{uploadSuccess}</Alert>}

        <Box sx={{ mt: 2 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            disabled={uploadLoading}
            sx={{ mb: 2 }}
          >
            {selectedFile ? selectedFile.name : 'Select Excel File'}
            <VisuallyHiddenInput type="file" onChange={handleFileChange} accept=".xlsx,.xls" />
          </Button>
          {uploadLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography>Uploading file...</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
      <CardActions sx={{ mt: 'auto', pl: 2, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={uploadLoading || !selectedFile}
          startIcon={uploadLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        >
          {uploadLoading ? 'Uploading...' : 'Upload'}
        </Button>
      </CardActions>
    </Card>
  );

  const renderComputeCard = () => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <CalculateIcon sx={{ mr: 1 }} /> Compute Metrics
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Calculate metrics and variances based on the uploaded metric data.
        </Typography>
        {computeError && <Alert severity="error">{computeError}</Alert>}
        {computeSuccess && <Alert severity="success">{computeSuccess}</Alert>}
      </CardContent>
      <CardActions sx={{ mt: 'auto', pl: 2, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CalculateIcon />}
          onClick={handleTriggerCompute}
          disabled={computeLoading}
        >
          {computeLoading ? 'Computing...' : 'Compute Metrics'}
        </Button>
      </CardActions>
    </Card>
  );

  const renderThresholdCard = () => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 1 }} /> Threshold Set-up
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure thresholds for various metrics to determine when values should trigger alerts.
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Coming soon...
        </Typography>
      </CardContent>
      <CardActions sx={{ mt: 'auto', pl: 2, pb: 2 }}>
        <Button variant="contained" color="primary" disabled>
          Configure
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <>
    <TitleBar title="" />
    <Container maxWidth="xl">

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 3 }}>
        <IconButton
          aria-label="Back"
          onClick={() => navigate('/home')}
          sx={{
            border: '1px solid #ccc',
            borderRadius: 2,
            p: 1,
            backgroundColor: '#f5f5f5',
            '&:hover': {
              backgroundColor: '#e0e0e0',
            },
            mr: 2,
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          View Metrics
        </Typography>
        <Button
          variant="outlined"
          onClick={refreshProgress}
          disabled={refreshing}
          startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{ ml: 'auto' }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh Status'}
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Upload Metrics */}
        <Grid item xs={12} md={6} lg={3}>
          {renderUploadCard()}
        </Grid>

        {/* Generate Metrics Template */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                  <DownloadIcon sx={{ mr: 1 }} /> Metrics Template
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Download a blank or sample Excel template for a review entity and metric level.
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-start' }}>
                <Button variant="contained" color="primary" onClick={() => setTemplateDialogOpen(true)}>
                  Generate
                </Button>
              </CardActions>
            </Card>
          </Paper>
        </Grid>

        {/* Compute Metrics */}
        <Grid item xs={12} md={6} lg={3}>
          {renderComputeCard()}
        </Grid>

        {/* View Input Metrics */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChartIcon sx={{ mr: 1 }} /> View Input Metrics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View uploaded metrics data for review entities.
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-start' }}>
                <Button variant="contained" color="primary" onClick={handleViewMetricsData}>
                  Go
                </Button>
              </CardActions>
            </Card>
          </Paper>
        </Grid>

        {/* View Computed Metrics */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                  <TimelineIcon sx={{ mr: 1 }} /> View Computed Metrics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View metrics computed from raw uploaded data.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}
{/* 
                {!isAdmin && (
                  <Autocomplete
                    sx={{ mt: 2 }}
                    options={reviewEntities}
                    getOptionLabel={(option) =>
                      `${option.name} — ${option.review_entity_type?.display_label ?? ''}`
                    }
                    value={reviewEntities.find(entity => entity.id === selectedEntity) || null}
                    onChange={handleAutocompleteChange}
                    disabled={loading}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name} — {option.review_entity_type?.display_label}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Review Entity"
                        placeholder="Search review entities..."
                      />
                    )}
                  />
                )} */}

                {!isAdmin && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Select a review entity to view its computed metrics
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-start' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleViewComputedMetrics}
                  disabled={loading}
                >
                  Go
                </Button>
              </CardActions>
            </Card>
          </Paper>
        </Grid>

        {/* Load & Compute History */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge
                    badgeContent={
                      (isActiveStatus(progressData?.status) ? 1 : 0) +
                      (isActiveStatus(computeProgressData?.status) ? 1 : 0)
                    }
                    color="warning"
                    invisible={
                      !isActiveStatus(progressData?.status) &&
                      !isActiveStatus(computeProgressData?.status)
                    }
                  >
                    <HistoryIcon sx={{ mr: 1 }} />
                  </Badge>
                  <Box component="span" sx={{ ml: 0.5 }}>Load &amp; Compute History</Box>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View in-progress loads and historical upload/compute results.
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-start' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/metrics-history')}
                >
                  View
                </Button>
              </CardActions>
            </Card>
          </Paper>
        </Grid>
      </Grid>

      {metricsStats && (
        <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Metrics Processing Results
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.light' }}>
                  <TableCell>Dimension</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(metricsStats).map(([metricType, stats]) => (
                  <TableRow key={metricType}>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{metricType}</TableCell>
                    <TableCell align="right">{stats?.total || 0}</TableCell>
                    <TableCell align="right">{stats?.success || 0}</TableCell>
                    <TableCell align="right">{stats?.failed || 0}</TableCell>
                    <TableCell align="right">
                      {stats?.total ? `${Math.round((stats.success / stats.total) * 100)}%` : '0%'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <MetricsTemplateDownloadDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        entities={templateEntities}
      />
    </Container>
    </>
  );
};

export default ViewMetrics;
